import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  getPackageListAsync,
  createPackageAsync,
  updatePackageAsync,
  deletePackageAsync,
} from '../actions/packageActions';

// ===================================================================
// Types
// ===================================================================

interface PackageItem {
  id: number;
  api_id: string | null;
  api_name: string;
  api_code: string;
  isActive: boolean;
  created_at: string;
  updated_at: string;
  additional_info: any[];
  [key: string]: any;
}

interface PaginationState {
  pageNumber: number;
  count: number;
}

interface PackageState {
  // List of package presets
  loading: boolean;
  packageList: PackageItem[];
  pagination: PaginationState;

  // Currently selected package (for edit drawer/modal)
  selectedPackage: PackageItem | null;

  // Mutation loading (create / update / delete)
  mutationLoading: boolean;
}

// ===================================================================
// Initial State
// ===================================================================

const initialState: PackageState = {
  loading: false,
  packageList: [],
  pagination: {
    pageNumber: 1,
    count: 0,
  },
  selectedPackage: null,
  mutationLoading: false,
};

// ===================================================================
// Slice
// ===================================================================

const packageSlice = createSlice({
  name: 'packageSlice',
  initialState,
  reducers: {
    // Set the package to be edited (populate form)
    setSelectedPackage: (state, action: PayloadAction<PackageItem | null>) => {
      state.selectedPackage = action.payload;
    },
    // Clear selected package (close drawer / cancel edit)
    clearSelectedPackage: (state) => {
      state.selectedPackage = null;
    },
    // Reset entire slice
    resetPackageState: () => initialState,
  },
  extraReducers: (builder) => {
    // ---------------------------------------------------------------
    // GET package list
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(getPackageListAsync.pending), (state) => {
      state.loading = true;
    });
    builder.addMatcher(isAnyOf(getPackageListAsync.rejected), (state) => {
      state.loading = false;
    });
    builder.addMatcher(isAnyOf(getPackageListAsync.fulfilled), (state, action) => {
      state.loading = false;
      const res = action.payload;
      state.packageList = res?.results?.data ?? [];
      state.pagination.count = res?.count ?? 0;
      state.pagination.pageNumber = action.meta.arg.page ?? 1;
    });

    // ---------------------------------------------------------------
    // CREATE package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(createPackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(createPackageAsync.rejected, createPackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );

    // ---------------------------------------------------------------
    // UPDATE package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(updatePackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(updatePackageAsync.rejected, updatePackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );

    // ---------------------------------------------------------------
    // DELETE package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(deletePackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(deletePackageAsync.rejected, deletePackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );
  },
});

export const { setSelectedPackage, clearSelectedPackage, resetPackageState } = packageSlice.actions;
export default packageSlice.reducer;
